import {
    ToggleButton,
    ToggleButtonGroup,
    Typography,
} from "@mui/material";
import {capitalize} from "@mui/material/utils";
import {useTranslation} from "react-i18next";
import {useState} from "react";
import {useFormikContext} from "formik";
import {Aufgabe, Tool} from "../../api";
import {useTools, useToolConfigsForAufgabe, useToolConfigsForAufgabeQueryKey} from "../../client/queries";
import {useDeleteToolConfig, useUpsertToolConfig} from "../../client/hooks";
import DialogToolConfig from "../tooling/DialogToolConfig";
import type {ToolConfigHooks} from "../tooling/DialogToolConfig";
import SectionToolsCard from "../tooling/SectionToolsCard";
import ToolListSection from "../tooling/ToolListSection";
import {TOOL_STAGE_STANDARD, TOOL_STAGE_INBOUND, TOOL_STAGE_TWILIO, type ToolMode} from "../../config/toolStages";
import type {AufgabeFormValues} from "../../types/aufgabenTypes";

interface Props {
    aufgabe: Aufgabe;
    canEdit: boolean;
}

export default function SectionToolsAufgabe({aufgabe, canEdit}: Props) {
    return (
        <SectionToolsCard>
            <SectionToolsAufgabeContent aufgabe={aufgabe} canEdit={canEdit}/>
        </SectionToolsCard>
    );
}

function SectionToolsAufgabeContent({aufgabe, canEdit}: Props) {
    const {t: translation} = useTranslation("aufgaben");
    const tools = useTools({limit: 100, mandantId: aufgabe.mandantId});
    const [configureTool, setConfigureTool] = useState<Tool | null>(null);
    const {values, setFieldValue} = useFormikContext<AufgabeFormValues>();

    const separateInbound = values.useSeparateToolsForInbound ?? false;
    const [mode, setMode] = useState<ToolMode>("standard");
    const effectiveMode: ToolMode = mode === "inbound" && !separateInbound ? "standard" : mode;
    const stageFilter = effectiveMode === "inbound"
        ? TOOL_STAGE_INBOUND
        : effectiveMode === "twilio"
            ? TOOL_STAGE_TWILIO
            : TOOL_STAGE_STANDARD;

    const activeIds = new Set(values.toolIds ?? []);

    const toggleTool = (toolId: string, active: boolean) => {
        const current = values.toolIds ?? [];
        const next = active
            ? [...current, toolId]
            : current.filter(id => id !== toolId);
        setFieldValue("toolIds", Array.from(new Set(next)).sort());
    };

    if (tools.items.length === 0) {
        return (
            <Typography variant="body2" color="text.secondary">
                {translation("tools-empty")}
            </Typography>
        );
    }

    const visibleTools = tools.items.filter(tool => tool.stage === stageFilter);

    return (
        <>
            <ToggleButtonGroup
                exclusive
                size="small"
                color="secondary"
                value={effectiveMode}
                onChange={(_, value) => { if (value) setMode(value as ToolMode); }}
                sx={{mb: 2}}
            >
                <ToggleButton value="standard">{capitalize(translation("tools-mode-standard"))}</ToggleButton>
                {separateInbound && (
                    <ToggleButton value="inbound">{capitalize(translation("tools-mode-inbound"))}</ToggleButton>
                )}
                <ToggleButton value="twilio">{capitalize(translation("tools-mode-twilio"))}</ToggleButton>
            </ToggleButtonGroup>
            <ToolListSection
                tools={visibleTools}
                activeIds={activeIds}
                canEdit={canEdit}
                onToggle={toggleTool}
                onConfigure={setConfigureTool}
                emptyLabel={translation("tools-empty")}
                configureLabel={translation("tools-configure")}
            />
            {configureTool && (
                <AufgabeToolConfigDialog
                    aufgabeId={aufgabe.id}
                    tool={configureTool}
                    onClose={() => setConfigureTool(null)}
                />
            )}
        </>
    );
}

function AufgabeToolConfigDialog({aufgabeId, tool, onClose}: { aufgabeId: string; tool: Tool; onClose: () => void }) {
    const upsertToolConfig = useUpsertToolConfig();
    const deleteToolConfig = useDeleteToolConfig();
    const hooks: ToolConfigHooks = {
        useConfigs: () => useToolConfigsForAufgabe(aufgabeId, tool.id),
        upsert: (key, data) => upsertToolConfig(aufgabeId, tool.id, key, data),
        remove: (key) => deleteToolConfig(aufgabeId, tool.id, key),
        queryKey: useToolConfigsForAufgabeQueryKey(aufgabeId, tool.id),
    };
    return (
        <DialogToolConfig
            open={true}
            onClose={onClose}
            tool={tool}
            hooks={hooks}
        />
    );
}
